<template>
  <layout :title="pageTitle">
    <section class="section">
      <div class="columns">
        <div class="column is-3">
          <ul class="inner-menu box">
            <li>
              <ScrollLink to="#overview">Overview</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#emails">Email Log</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#projects">Projects</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#participants">Participants</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#feedbackgivers">Feedback Givers</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#actions">Actions</ScrollLink>
            </li>
          </ul>
        </div>

        <div class="column is-9">
          <b-notification
            v-if="filteredByProject"
            type="is-warning is-light"
            aria-close-label="Close notification"
            role="alert"
            @close="clearProjectFilter"
          >
            This page is currently being filtered by a specific project. If you
            want to see all relevant data then you can close this notification
            to remove the filter.
          </b-notification>

          <div class="box">
            <a class="anchor" id="overview"></a>
            <h3>Overview</h3>
            <hr style="margin-top: 15px" />
            <div class="columns">
              <div class="column">
                <b-field
                  label="First Name"
                  :type="{ 'is-danger': $page.props.errors?.first_name }"
                  :message="$page.props.errors?.first_name"
                >
                  <b-input
                    @blur="updateUser"
                    v-model="form.first_name"
                  ></b-input>
                </b-field>
              </div>
              <div class="column">
                <b-field
                  label="Last Name"
                  :type="{ 'is-danger': $page.props.errors?.last_name }"
                  :message="$page.props.errors?.last_name"
                >
                  <b-input
                    @blur="updateUser"
                    v-model="form.last_name"
                  ></b-input>
                </b-field>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <b-field
                  label="Email"
                  :type="{ 'is-danger': $page.props.errors?.email }"
                  :message="$page.props.errors?.email"
                >
                  <b-input @blur="updateUser" v-model="form.email"></b-input>
                </b-field>
              </div>
            </div>
          </div>

          <div class="box">
            <a class="anchor" id="emails"></a>
            <h3>Email Log</h3>
            <hr style="margin-top: 15px" />
            <div class="columns">
              <div class="column">
                <email-log-table :emailLogs="user.email_logs" />
              </div>
            </div>
          </div>

          <div class="box">
            <a class="anchor" id="projects"></a>
            <h3>Associated Projects</h3>
            <hr style="margin-top: 15px" />
            <view-projects-table :projects="projects" />
          </div>

          <div class="box">
            <a class="anchor" id="participants"></a>
            <h3>Associated Participants</h3>
            <a class="anchor" id="emails"></a>
            <hr style="margin-top: 15px" />
            <view-users-table
              :users="participants"
              :project="project"
              userType="participants"
              :user="user"
            />
          </div>

          <div class="box">
            <a class="anchor" id="feedbackgivers"></a>
            <div class="title-bar">
              <h3>Associated Feedback Givers</h3>
              <div v-if="filteredByProject" class="buttons">
                <b-button
                  class="is-primary"
                  @click="
                    showLinkFeedbackGiverModal = !showLinkFeedbackGiverModal
                  "
                  >Link
                </b-button>
              </div>
            </div>
            <hr style="margin-top: 15px" />
            <view-users-table
              :users="feedbackGivers"
              :project="project"
              progressLabel="Received"
              userType="feedbackGivers"
              :user="user"
            />
          </div>

          <div class="box">
            <a class="anchor" id="actions"></a>
            <h3>Actions</h3>
            <hr style="margin-top: 15px" />
            <a href="mailto:{user.email}"
              ><b-button type="is-info">Send Personal Email</b-button></a
            >
            <inertia-link
              :href="route('useractions.passwordReset', user.unique_id)"
            >
              <b-button type="is-info">Send Password Reset Email</b-button>
            </inertia-link>
            <b-button @click="showPasswordResetModal = 1" type="is-info"
              >Manual Password Reset</b-button
            >
            <inertia-link
              :href="route('useractions.impersonateUser', user.unique_id)"
            >
              <b-button type="is-info"> Impersonate </b-button>
            </inertia-link>
            <hr />
            <b-button @click="showMergeUserModal" type="is-warning"
              >Merge User</b-button
            >
          </div>
        </div>
      </div>
    </section>

    <b-modal
      v-model="showPasswordResetModal"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-label="Change User Password"
      aria-modal
    >
      <template>
        <form @submit.prevent="submitPasswordReset">
          <div class="columns">
            <div class="column is-12">
              <div class="box">
                <a id="overview"></a>
                <h3>Change User Password</h3>
                <hr style="margin-top: 15px" />
                <div class="columns">
                  <div class="column">
                    <b-field
                      label="Password"
                      :message="errors?.password"
                      :type="{ 'is-danger': errors?.password }"
                    >
                      <b-input
                        type="password"
                        password-reveal
                        v-model="passwordResetForm.password"
                      ></b-input>
                    </b-field>
                    <b-field
                      label="Confirm Password"
                      :message="errors?.password_confirmation"
                      :type="{ 'is-danger': errors?.password_confirmation }"
                    >
                      <b-input
                        type="password"
                        password-reveal
                        v-model="passwordResetForm.password_confirmation"
                      ></b-input>
                    </b-field>
                  </div>
                </div>
                <div class="columns">
                  <div class="column">
                    <button
                      type="submit"
                      class="button is-primary"
                      @click="submitPasswordReset"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </template>
    </b-modal>

    <b-modal
      v-model="showLinkFeedbackGiverModal"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <LinkFeedbackGiverModal
        :project="project"
        :participant="user"
        :feedbackGivers="feedbackGivers"
      />
    </b-modal>
  </layout>
</template>

<script>
import Layout from './Layout';
import ScrollLink from '@/Components/Shared/ScrollLink';
import ViewUsersTable from '../../Components/Management/ViewUsersTable';
import ViewProjectsTable from '../../Components/Management/ViewProjectsTable';
import EmailLogTable from '../../Components/Management/EmailLogTable';
import LinkFeedbackGiverModal from '../../Components/Management/Modals/LinkFeedbackGiverModal';
import MergeUserModal from '../../Components/Management/Modals/MergeUserModal';

export default {
  components: {
    Layout,
    ScrollLink,
    ViewUsersTable,
    ViewProjectsTable,
    EmailLogTable,
    LinkFeedbackGiverModal,
    MergeUserModal,
  },
  props: {
    user: Object,
    projects: Array,
    project: Object,
    feedbackGivers: Array,
    participants: Array,
    errors: { type: Object },
  },
  data() {
    return {
      pageTitle:
        'Edit User: ' + this.user.first_name + ' ' + this.user.last_name,
      form: {
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        email: this.user.email,
      },
      passwordResetForm: {
        password: '',
        password_confirmation: '',
      },
      showPasswordResetModal: false,
      filteredByProject: false,
      showLinkFeedbackGiverModal: false,
    };
  },
  mounted() {
    this.filteredByProject = new URLSearchParams(window.location.search).has(
      'project'
    );
  },
  methods: {
    clearProjectFilter() {
      this.$inertia.get(route('users.show', this.user.unique_id));
    },
    updateUser() {
      this.$inertia.put(route('users.update', this.user.unique_id), this.form);
    },
    submitPasswordReset() {
      this.$inertia.put(
        route('users.manualUpdatePassword', this.user.unique_id),
        this.passwordResetForm,
        {
          preserveScroll: true,
          onSuccess: () => {
            this.showPasswordResetModal = 0;
            this.$buefy.toast.open({
              message: 'Password Reset Successfully',
              type: 'is-success',
              position: 'is-top',
            });
          },
        }
      );
    },
    showMergeUserModal() {
      this.$buefy.modal.open({
        parent: this,
        props: {
          selectedToMerge: this.user,
        },
        component: MergeUserModal,
        fullScreen: false,
        trapFocus: true,
        canCancel: ['x', 'escape'],
      });
    },
  },
};
</script>
